import { ImpactBadge, Skeleton, Text, TextColor, TextSize, TextVariant, TextWeight } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import * as React from 'react';

interface BaseMetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The label/title for the metric
   */
  label: string;
  /**
   * Optional description text to display below the title
   */
  description?: string;
  /**
   * Number of filled zaps for impact badge (0-4)
   */
  filledZapCount: number;
  /**
   * The chart component to render
   */
  children: React.ReactNode;
  /**
   * Optional content to display in the header (e.g., score display)
   */
  additionalHeaderContent?: React.ReactNode;
  /**
   * Optional footer content to display below the chart (e.g., action buttons)
   */
  footer?: React.ReactNode;
  /**
   * Whether the card is in loading state
   * @default false
   */
  isLoading?: boolean;
}

/**
 * BaseMetricCard provides the common structure for all metric card variants
 * Includes header with label, description, impact badge, chart area, and optional footer
 */
const BaseMetricCard = React.forwardRef<HTMLDivElement, BaseMetricCardProps>((props, forwardedRef) => {
  const {
    label,
    description,
    filledZapCount,
    children,
    additionalHeaderContent,
    footer,
    isLoading = false,
    className,
    ...other
  } = props;

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx('flex flex-col gap-3 justify-between p-4 bg-gray-light-25', className)}
        {...other}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 flex-1">
            <Skeleton variant="square" className="w-24 h-7" />
            <Skeleton variant="square" className="w-1/2 h-12" />
          </div>
          <Skeleton variant="square" className="w-22 h-5" />
        </div>

        <div className="w-full">
          <Skeleton variant="square" className="w-full h-[60px]" />
        </div>

        {/* Footer space to match non-loading state */}
        <div className="text-xs h-[1.2em]" />
      </div>
    );
  }

  return (
    <div
      ref={forwardedRef}
      className={cx('flex flex-col gap-3 justify-between p-4 bg-gray-light-25', className)}
      {...other}
    >
      {/* Header: Title, Description, Optional Content, and Impact Badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <Text variant={TextVariant.Label} className="text-gray-utility-500" showEllipsis={false}>
            {label}
          </Text>
          {description && (
            <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Tertiary} showEllipsis={false}>
              {description}
            </Text>
          )}
          {additionalHeaderContent}
        </div>
        <ImpactBadge filledCount={filledZapCount} />
      </div>

      {/* Chart */}
      <div className="w-full">{children}</div>

      {/* Optional Footer */}
      {footer}
    </div>
  );
});

BaseMetricCard.displayName = 'BaseMetricCard';

export type { BaseMetricCardProps };
export { BaseMetricCard };
